import { inject, Injectable } from '@angular/core'
import { HearingSectionEventEffect } from '@ftr/annotations-contracts/v2'
import { VocabularyTerm } from '@ftr/contracts/type/core'
import { Uuid } from '@ftr/contracts/type/shared'
import { firstResultFrom, ToastService } from '@ftr/foundation'
import { VocabularyTermsService } from '@ftr/ui-vocab'
import { Action, State, StateContext } from '@ngxs/store'
import { firstValueFrom } from 'rxjs'
import { HearingSectionEventsService, HearingSectionEventType } from '../../services'
import { FetchPeopleAnnotationsCommand } from '../people-annotations/people-annotations.commands'
import {
  CreateHearingSectionEventCommand,
  DeleteHearingSectionEventCommand,
  HearingSectionEventsChangedEvent,
  UpdateHearingSectionEventContentCommand,
} from './hearing-section-events.commands'

type HearingSectionEventsStateModel = Record<string, never>

@State<HearingSectionEventsStateModel>({
  name: 'hearingSectionEvents',
  defaults: {},
})
@Injectable()
export class HearingSectionEventsState {
  private readonly hearingSectionEventsService = inject(HearingSectionEventsService)
  private readonly toastService = inject(ToastService)
  private readonly vocabularyTermsService = inject(VocabularyTermsService)

  @Action(CreateHearingSectionEventCommand)
  private async create(
    ctx: StateContext<HearingSectionEventsStateModel>,
    { courtSystemId, body }: CreateHearingSectionEventCommand,
  ): Promise<void> {
    try {
      const result = await firstResultFrom(
        this.hearingSectionEventsService.createHearingSectionEvent(courtSystemId, body),
      )
      ctx.dispatch(new HearingSectionEventsChangedEvent())
      const shownEffects = await this.showEffectToasts(result?.metadata?.effects, courtSystemId)
      if (!shownEffects) {
        this.toastService.success('An event has been added.', undefined, { hideTitle: true })
      }
    } catch {
      this.toastService.error(
        body.eventType === HearingSectionEventType.APPEARANCE
          ? 'Failed to add appearance to hearing. Please try again.'
          : 'Failed to add event to hearing. Please try again.',
      )
    }
  }

  @Action(UpdateHearingSectionEventContentCommand)
  private async updateContent(
    ctx: StateContext<HearingSectionEventsStateModel>,
    { courtSystemId, eventId, resources, recordingId }: UpdateHearingSectionEventContentCommand,
  ): Promise<void> {
    try {
      const result = await firstResultFrom(
        this.hearingSectionEventsService.updateHearingSectionEvent(courtSystemId, eventId, { resources }),
      )
      ctx.dispatch(new HearingSectionEventsChangedEvent())
      // Re-linking the appearance's person/role changes the people list, so refresh it.
      if (result?.item?.eventType === HearingSectionEventType.APPEARANCE && recordingId) {
        ctx.dispatch(new FetchPeopleAnnotationsCommand(courtSystemId, recordingId, { force: true }))
      }
      const shownEffects = await this.showEffectToasts(result?.metadata?.effects, courtSystemId)
      if (!shownEffects) {
        this.toastService.success('An event has been updated.', undefined, { hideTitle: true })
      }
    } catch {
      this.toastService.error('Failed to update event on hearing. Please try again.')
    }
  }

  @Action(DeleteHearingSectionEventCommand)
  private async delete(
    ctx: StateContext<HearingSectionEventsStateModel>,
    { courtSystemId, eventId }: DeleteHearingSectionEventCommand,
  ): Promise<void> {
    try {
      const result = await firstResultFrom(
        this.hearingSectionEventsService.deleteHearingSectionEvent(courtSystemId, eventId),
      )
      ctx.dispatch(new HearingSectionEventsChangedEvent())
      const shownEffects = await this.showEffectToasts(result?.metadata?.effects, courtSystemId)
      if (!shownEffects) {
        this.toastService.success('An event has been removed.', undefined, { hideTitle: true })
      }
    } catch {
      this.toastService.error('Failed to remove event from hearing. Please try again.')
    }
  }

  /**
   * Toasts each server-described side effect, replacing `{{vocabTerm}}` placeholders (e.g.
   * `{{hearing}}`) with the court system's configured term. Returns whether any toast was shown.
   */
  private async showEffectToasts(
    effects: HearingSectionEventEffect[] | undefined,
    courtSystemId: Uuid,
  ): Promise<boolean> {
    const describedEffects = (effects ?? []).filter(
      (effect): effect is HearingSectionEventEffect & { description: string } => !!effect.description,
    )
    if (!describedEffects.length) {
      return false
    }

    const terms = (await firstValueFrom(this.vocabularyTermsService.observeTerms(courtSystemId))) as unknown as Record<
      string,
      VocabularyTerm | undefined
    >
    describedEffects.forEach(effect => {
      const message = effect.description.replace(
        /\{\{(\w+)\}\}/g,
        (token, key: string) => terms[key]?.singular ?? token,
      )
      this.toastService.success(message, undefined, { hideTitle: true })
    })
    return true
  }
}
